import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { useUserReviews } from '@/scopes/products/user-reviews/hooks'
import { Button, Icon, tk, Typography } from '@sunwebgroup/ui-components'
import React, { memo, useCallback } from 'react'
import { useIntl } from 'react-intl'
import { styled } from 'styled-components'
import { createUserReviewsEvent } from '../../../../../user-reviews/infrastructure/analytics'

const Wrapper = styled.div`
  position: relative;
  display: flex;
`

const SelectIcon = styled(Icon)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;

  ${Icon.modSize('md')}

  @media ${tk('mq_desktop_md')} {
    right: ${tk('spacing_xs')};
  }
`

const Select = styled(Button)<{ $isLoading?: boolean }>`
  text-align: left;
  text-align: -webkit-left;
  padding-right: ${tk('spacing_xl')};

  ${Typography.modVariant('label')}

  &:focus-visible {
    outline: none;
    box-shadow: none;
  }

  @media ${tk('mq_desktop_md')} {
    padding-left: 0;
    text-align: right;
    text-align: -webkit-right;
  }

  // Firefox cuts off the text when using letter-spacing, so we remove it
  @supports (-moz-appearance: none) {
    letter-spacing: 0;
  }

  ${({ $isLoading }) => $isLoading && `pointer-events: none;`}
`

const SORT_OPTIONS = [
  {
    id: '-date',
    translation: 'reviews_sort_option_most_recents',
  },
  {
    id: 'date',
    translation: 'reviews_sort_option_less_recents',
  },
  {
    id: '-rating',
    translation: 'reviews_sort_option_higher_score_to_lower',
  },
  {
    id: 'rating',
    translation: 'reviews_sort_option_lower_score_to_higher',
  },
]

export const SortingSelect = memo(() => {
  const intl = useIntl()
  const { sorting, setSorting } = useUserReviews()
  const { sendEvent } = useAnalytics()
  const getResultSorting: Record<string, string> = {
    '-date': 'Most recent',
    'date': 'Least recent',
    '-rating': 'Highest score to lowest',
    'rating': 'Lowest score to highest',
  }

  const handleChangeSorting: React.ChangeEventHandler<HTMLSelectElement> = useCallback(
    (e) => {
      sendEvent(
        createUserReviewsEvent({
          description: 'sort reviews',
          element: 'Dropdown',
          result: getResultSorting[e.target.value],
        }),
      )
      setSorting(e.target.value)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sendUserReviewsEvent is a stable function
    [setSorting],
  )

  return (
    <Wrapper>
      <Select
        as="select"
        $hierarchy="tertiary"
        $size="default"
        $withoutColor
        value={sorting}
        onChange={handleChangeSorting}
      >
        {SORT_OPTIONS.map(option => (
          <option value={option.id} key={option.id}>
            {intl.formatMessage({ id: option.translation })}
          </option>
        ))}
      </Select>
      <SelectIcon glyph="IconChevronDownSmall" />
    </Wrapper>
  )
})
SortingSelect.displayName = 'SortingSelect'
